import React from "react";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import { LicenseClaim } from "./Claim";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { CLAIM_LICENSE, GET_LICENSE, LicenseResult } from "../../Queries";

interface ComponentProps {
  jwt?: string;
  children?: React.ReactNode;
}

/** Payment modes: IN = institution, ST = student code, IO = inclusive opt-in, OO = inclusive opt-out */
const INCLUSIVE_OPTIN = "IO";
const INCLUSIVE_OPTOUT = "OO";

const LicenseGate: React.FC<ComponentProps> = ({ jwt, children }) => {
  const [t] = useTranslation();
  const { loading: roleLoading, data: roleData } = useQuery<LicenseResult>(
    GET_LICENSE,
    {
      context: {
        headers: {
          Authorization: `JWT ${jwt}`,
        },
      },
    },
  );

  const [claimLicense, { loading: claimPending, error: claimError }] =
    useMutation(CLAIM_LICENSE, {
      refetchQueries: ["MyLicense"],
    });

  if (roleLoading) {
    return <div>{t("modals.licenseGate.loading")}</div>;
  }

  const isLicensed = !roleData?.myLicense.includes("unlicensed");
  const paymentMode = roleData?.paymentMode ?? null;
  const isInclusiveOptIn = paymentMode === INCLUSIVE_OPTIN;
  const isInclusiveOptOut = paymentMode === INCLUSIVE_OPTOUT;

  const errorMessage = claimError && claimError.message;

  async function handleClaim(tokenString: string) {
    await claimLicense({
      variables: {
        tokenString,
      },
      context: {
        headers: {
          Authorization: `JWT ${jwt}`,
        },
      },
    });
  }

  if (isLicensed) {
    return <>{children}</>;
  }

  // Inclusive access modes: show message only (no code entry)
  if (isInclusiveOptIn) {
    return (
      <Dialog open>
        <DialogTitle>{t("modals.licenseGate.optInTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("modals.licenseGate.optInBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled>{t("modals.licenseGate.continue")}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (isInclusiveOptOut) {
    return (
      <Dialog open>
        <DialogTitle>{t("modals.licenseGate.optOutTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("modals.licenseGate.optOutBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled>{t("modals.licenseGate.continue")}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Student code mode: show code entry
  return (
    <LicenseClaim
      onClaim={handleClaim}
      isPending={claimPending}
      errorMessage={errorMessage}
    />
  );
};

export default LicenseGate;
